import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, FileText, Eye } from 'lucide-react';
import { searchApi } from '../lib/api';
import { formatDate, formatCurrency } from '../utils/format';

export default function Search() {
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['search', searchQuery],
    queryFn: () => searchApi.search({ q: searchQuery }),
    enabled: searchQuery.length > 0,
  });

  const documents = data?.data?.documents || [];
  const total = data?.data?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(query);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Search Documents</h1>
        <p className="text-gray-600">
          Full-text search across all documents and receipts
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="card">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by vendor, amount, description, or any text..."
              className="input pl-10"
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </div>
      </form>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : searchQuery && documents.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No documents found matching "{searchQuery}"</p>
          <p className="text-sm text-gray-400 mt-2">
            Try different keywords or check your spelling
          </p>
        </div>
      ) : searchQuery && documents.length > 0 ? (
        <div className="card">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Found {total} document{total !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          </div>

          <div className="space-y-4">
            {documents.map((doc: any) => (
              <div
                key={doc.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <h3 className="font-medium text-gray-900">{doc.filename}</h3>
                      <span className="badge-gray uppercase text-xs">
                        {doc.file_type}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {doc.vendor_name && (
                        <span>Vendor: {doc.vendor_name}</span>
                      )}
                      {doc.total_amount && (
                        <span className="font-medium">
                          {formatCurrency(doc.total_amount)}
                        </span>
                      )}
                      {doc.document_date && (
                        <span>{formatDate(doc.document_date)}</span>
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/documents/${doc.id}`}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <SearchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Enter a search query to find documents</p>
          <p className="text-sm text-gray-400 mt-2">
            Search by vendor, amount, date, or any text in your documents
          </p>
        </div>
      )}

      {/* Search Tips */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Search Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Search by vendor name: "John Deere", "Walmart"</li>
          <li>• Search by amount: "$50", "100.00"</li>
          <li>• Search by category: "fuel", "fertilizer", "parts"</li>
          <li>• Search by check number: "check 1234"</li>
          <li>• Use quotes for exact phrases: "farm supplies"</li>
        </ul>
      </div>
    </div>
  );
}

